import { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

interface LiveStreamDemoProps {
  cameraId: string;
}

export const LiveStreamDemo = ({ cameraId }: LiveStreamDemoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStreamUrl = async () => {
      try {
        const response = await fetch(
          `https://nk6jtwgw3l.execute-api.eu-west-2.amazonaws.com/farmstream/internal/livestream-url?camera_id=${cameraId}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to load stream URL');
        }
        
        const url = await response.text();
        
        if (videoRef.current) {
          videoRef.current.src = url;
          setIsLoading(false);
        }
      } catch (err) {
        console.error(err);
        setError('Could not load the livestream.');
        setIsLoading(false);
      }
    };

    fetchStreamUrl();
  }, [cameraId]);

  return (
    <VideoWrapper>
      <StyledVideo
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        style={{ display: isLoading || error ? 'none' : 'block' }}
      />
      
      {(isLoading || error) && (
        <LoadingContainer>
          {isLoading && <Spinner />}
          <LoadingText>
            {error || 'Loading livestream...'}
          </LoadingText>
        </LoadingContainer>
      )}
    </VideoWrapper>
  );
};

// Styled Components
const VideoWrapper = styled.div`
  width: 100%;
  max-width: 720px;
  background-color: #000;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  margin: 0 auto;
  /* No fixed aspect ratio - let video determine its own size */
`;

const StyledVideo = styled.video`
  width: 100%;
  height: auto; /* Let video maintain its natural aspect ratio */
  display: block;
  border: none;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
  margin-top: 20px;
`;

const LoadingText = styled.div`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.gray900};
  margin-top: 8px;
  text-align: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 200px; /* Only minimum height for loading spinner */
  padding: 2rem;
  position: relative;
  z-index: 1;
`;
